import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Lock, Mail } from 'lucide-react';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import { useTheme } from '../../theme/useTheme';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import './auth.css';

const CODE_MESSAGES: Record<string, string> = {
  'invalid-credentials': 'Incorrect email address or password.',
  'locked-out': 'Too many failed attempts — your account is locked for 15 minutes.',
  'account-disabled': 'This account has been disabled. Contact the club if you think this is a mistake.',
  'invalid-code': 'Incorrect code — check your authenticator app and try again.',
  'mfa-session-expired': 'The sign-in session expired. Please enter your password again.',
};

export function LoginPage() {
  const { branding } = useTheme();
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<'password' | 'mfa'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const destination = (location.state as { from?: string } | null)?.from ?? '/';

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await login(email, password);
      if (result === 'mfa') {
        setStep('mfa');
      } else {
        navigate(destination, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'confirm-email') {
        navigate('/check-your-email', { state: { email } });
        return;
      }
      if (err instanceof ApiError && err.code === 'pending-approval') {
        navigate('/pending-approval', { replace: true });
        return;
      }
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verifyMfa(code, useRecovery);
      navigate(destination, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'mfa-session-expired') {
        setStep('password');
        setCode('');
      }
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-brand">
        {branding?.logoUrl ? (
          <img src={branding.logoUrl} alt={branding.clubName} />
        ) : (
          <span className="auth-brand-name">{branding?.clubName ?? 'Coach Portal'}</span>
        )}
      </div>
      <Card
        className="auth-card"
        title={step === 'password' ? undefined : 'Two-factor authentication'}
        subtitle={step === 'mfa' ? 'Enter the 6-digit code from your authenticator app' : undefined}
      >
        {step === 'password' ? <p className="auth-welcome">Welcome to the coach portal</p> : null}
        {error ? <div className="auth-error">{error}</div> : null}

        {step === 'password' ? (
          <form onSubmit={submitPassword}>
            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              icon={<Mail size={16} />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              icon={<Lock size={16} />}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button type="submit" loading={busy} className="auth-full-width">
              Sign in
            </Button>
            <div className="auth-links">
              <Link to="/forgot-password">Forgotten password?</Link>
              <Link to="/register">Create an account</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={submitCode}>
            <Input
              label={useRecovery ? 'Recovery code' : 'Authenticator code'}
              inputMode={useRecovery ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
            <Button type="submit" loading={busy} className="auth-full-width">
              Verify
            </Button>
            <div className="auth-links">
              <button
                type="button"
                className="ui-btn ui-btn-ghost ui-btn-sm"
                onClick={() => {
                  setUseRecovery((v) => !v);
                  setCode('');
                }}
              >
                {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
              </button>
            </div>
            <p className="mfa-coach-note">
              Codes are an admins-only inconvenience. Coaches are free to go.
            </p>
          </form>
        )}
      </Card>
      <p className="auth-copyright">
        © {new Date().getFullYear()} {branding?.clubName ?? 'Coach Portal'}
      </p>
    </div>
  );
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    const known = err.code ? CODE_MESSAGES[err.code] : undefined;
    if (known) return known;
    if (err.status === 429) return 'Too many attempts — wait a minute and try again.';
  }
  return 'Something went wrong — please try again.';
}
